import { type ComponentPropsWithoutRef, useEffect, useState } from "react"
import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import "./PrivateShowAcceptDenyModal.scss"
import { Sizes } from "../../../interfaces/sizing"
import { useAppSelector } from "../../../store/hooks"
import { selectRoomPrivateRequestStatus } from "../../../store/roomSlice"
import { colorClass } from "../../../utils/css"
import { addPageAction } from "../../../utils/newrelic"
import { Button, ButtonColor, ButtonSize, Typography } from "../../common"
import { ConfirmModal } from "../../common/molecules/ConfirmModal"
import { Alignment } from "../../common/molecules/ConfirmModal/ConfirmModal"
import { approvePrivateShow, declineOrEndPrivateShow } from "../api"

type PrivateShowAcceptDenyModalProps = ComponentPropsWithoutRef<"div">

export function PrivateShowAcceptDenyModal({
    ...props
}: PrivateShowAcceptDenyModalProps) {
    const privateShowRequestStatus = useAppSelector(
        selectRoomPrivateRequestStatus,
    )
    const i18n = {
        accept: t`Accept`,
        deny: t`Deny`,
    }
    const user = privateShowRequestStatus?.requester ?? ""
    const isPremium = privateShowRequestStatus?.isPremium ?? false
    const [showDenyConfirmModal, setShowConfirmModal] = useState(false)

    useEffect(() => {
        addPageAction("MobileBroadcastPrivateShowModalDisplayed")
    }, [])

    const handleAccept = () => {
        addPageAction("MobileBroadcastPrivateShowAccepted")
        void approvePrivateShow()
    }

    const handleDenyClick = () => {
        setShowConfirmModal(true)
    }

    const denyPrivateShowRequest = () => {
        setShowConfirmModal(false)
        addPageAction("MobileBroadcastPrivateShowDenied")
        void declineOrEndPrivateShow()
    }

    return (
        <>
            <div className="PrivateShowAcceptDenyModal" {...props}>
                <Typography
                    size={Sizes.smpx}
                    className="PrivateShowAcceptDenyModal__text"
                    color={colorClass.whiteFontColor}
                    data-testid="pvt-show-request-text"
                >
                    <Trans>
                        <span>{user}</span> is requesting a{" "}
                        {isPremium ? "Premium " : ""}Private Show
                    </Trans>
                </Typography>
                <div className="PrivateShowAcceptDenyModal__buttons">
                    <Button
                        className="PrivateShowAcceptDenyModal__buttons__deny"
                        data-testid="pvt-show-deny-button"
                        onClick={handleDenyClick}
                        size={ButtonSize.Small}
                        color={ButtonColor.Clear}
                        text={i18n.deny}
                    />
                    <Button
                        className="PrivateShowAcceptDenyModal__buttons__accept"
                        data-testid="pvt-show-accept-button"
                        onClick={handleAccept}
                        size={ButtonSize.Small}
                        color={ButtonColor.Orange}
                        text={i18n.accept}
                    />
                </div>
            </div>
            {showDenyConfirmModal && (
                <ConfirmModal
                    isOpen={showDenyConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    onConfirm={denyPrivateShowRequest}
                    message=""
                    title={t`Are you sure you want to deny the request?`}
                    confirmText={t`Confirm`}
                    cancelText={t`Cancel`}
                    textAlign={Alignment.left}
                />
            )}
        </>
    )
}
